// CRM layer — the OPPORTUNITIES cell on the calls table.
//
// Phase 6-C: this cell now does opportunities ONLY. The transfer action moved
// to its own column (CrmTransferCell), so the two concerns are not conflated.
//
// Creating an opportunity is always a DELIBERATE act: there is no pending or
// suggested limbo state. The empty state is a "فتح فرصة" button that opens the
// start flow, and a real opportunity exists only once the agent confirms —
// assigned to them, at stage `new`.
//
// It reads from an opportunity map the host page fetches ONCE, rather than
// calling /suggest per row: 50 rows would otherwise mean 50 requests per page
// load. The authoritative suggestion is fetched on demand, on click.

import React, { useState, useEffect } from 'react';
import { FiPlusCircle, FiUser, FiBell, FiUserPlus, FiRepeat } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import CrmStartOpportunityModal from './CrmStartOpportunityModal';
import CrmSaveClientModal from './CrmSaveClientModal';
import CrmReassignModal from './CrmReassignModal';
import { STAGE_LABEL, stageBadgeStyle, crmNav, typeLabel, isPendingClassification } from './crmApi';
import { hasPerm } from './perms';
import { modalOpen, modalClosed } from './modalGuard';

// How many opportunities fit before we collapse the rest into a "+N" chip.
const VISIBLE_BADGES = 2;

// Reminder chip colours: amber while there is still room to act, red and
// pulsing on the final reminder — the next engine run unassigns it.
const REMINDER_STYLE = { background: '#fdf3e7', color: '#b25e09', fontWeight: 700 };
const REMINDER_HOT_STYLE = { background: '#fdeceb', color: '#b42318', fontWeight: 700 };
// Matches the board/preview convention; max_reminders is 3 by default.
const HOT_AT = 3;

const CrmCallCell = ({ call, activeByPhone, savedPhones, onOpenClient, onOpenOpportunity, onChanged }) => {
  const { user } = useAuth();
  const [showStart, setShowStart] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [reassignFor, setReassignFor] = useState(null);   // opportunity being reassigned

  // Item 6: pause the host page's 30s auto-refresh while a modal here is open.
  useEffect(() => {
    if (showStart || showSave) { modalOpen(); return () => modalClosed(); }
    return undefined;
  }, [showStart, showSave]);

  const canOpen = hasPerm(user, 'open_opportunity');
  const canAssign = hasPerm(user, 'assign_opportunity');   // manager: reassign at any stage
  const phone = call.callerNumber;
  const active = (activeByPhone && activeByPhone[phone]) || [];
  // One button, two states: a saved client gets "الملف", an unsaved one gets
  // "حفظ العميل". Replaces the old standalone "عميل" status column.
  const hasProfile = !savedPhones || savedPhones.has(phone);
  const shown = active.slice(0, VISIBLE_BADGES);
  const hidden = active.length - shown.length;

  const openClient = () => {
    crmNav.setClient(phone);
    if (onOpenClient) onOpenClient(phone);
  };
  const openOpportunity = (id) => {
    crmNav.setOpportunity(id);
    if (onOpenOpportunity) onOpenOpportunity(id);
  };

  return (
    <div className="crm-scope" style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 150 }}>
      {active.length > 0 ? (
        <>
          {/* One row per opportunity: type, stage and reminders are SEPARATE
              badges rather than one run-on line, so each reads at a glance. */}
          {shown.map((o) => {
            const hot = o.reminder_count >= HOT_AT;
            return (
              <div key={o.id} style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  className="badge crm-badge"
                  title={`${typeLabel(o.opportunity_type)} — ${STAGE_LABEL[o.crm_stage]}`}
                  style={{ background: '#eef1f5', color: '#334155', cursor: 'pointer', textAlign: 'right', fontWeight: 600 }}
                  onClick={(e) => { e.stopPropagation(); openOpportunity(o.id); }}
                >
                  {typeLabel(o.opportunity_type)}
                </button>
                {/* Batch 3: an untriaged row gets the pending pill instead of a
                    stage badge — on the calls list this is the agent's cue that
                    the call they just handled still needs classifying. */}
                <button
                  className={isPendingClassification(o) ? 'crm-pill crm-pill-pending' : 'badge crm-badge'}
                  title={isPendingClassification(o)
                    ? 'هذا التواصل بانتظار التصنيف'
                    : `المرحلة: ${STAGE_LABEL[o.crm_stage]}`}
                  style={isPendingClassification(o)
                    ? { cursor: 'pointer' }
                    : { ...stageBadgeStyle(o.crm_stage), cursor: 'pointer' }}
                  onClick={(e) => { e.stopPropagation(); openOpportunity(o.id); }}
                >
                  {STAGE_LABEL[o.crm_stage]}
                </button>
                {o.reminder_count > 0 && (
                  <button
                    className={`badge crm-badge${hot ? ' crm-rem-hot' : ''}`}
                    title={hot
                      ? `${o.reminder_count} تذكيرات — آخر تذكير قبل سحب الإسناد`
                      : `${o.reminder_count} تذكير آلي دون تحديث المرحلة`}
                    style={{ ...(hot ? REMINDER_HOT_STYLE : REMINDER_STYLE), cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); openOpportunity(o.id); }}
                  >
                    <FiBell size={10} style={{ verticalAlign: 'middle', marginLeft: 3 }} />
                    {o.reminder_count}
                  </button>
                )}
                {o.has_unread && (
                  <button className="crm-unread" title="تعليق داخلي جديد — افتح الطلب للرد"
                    style={{ border: 'none', cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); openOpportunity(o.id); }}>
                    <span className="crm-unread-dot" /> تعليق جديد
                  </button>
                )}
                {/* Item 1: manager reassignment, at any stage. */}
                {canAssign && (
                  <button
                    className="btn btn-outline btn-sm"
                    title="إعادة إسناد هذا الطلب لموظف آخر"
                    style={{ fontSize: 10, padding: '1px 6px' }}
                    onClick={(e) => { e.stopPropagation(); setReassignFor(o); }}
                  >
                    <FiRepeat size={10} /> إعادة إسناد
                  </button>
                )}
              </div>
            );
          })}
          {/* Never hide data silently: the overflow is counted and clickable,
              opening the client profile where all of them are listed. */}
          {hidden > 0 && (
            <button
              className="badge crm-badge"
              title={`${hidden} طلب نشط إضافي — افتح ملف العميل لعرضها كلها`}
              style={{ background: '#eceff1', color: '#455a64', border: 'none', cursor: 'pointer', textAlign: 'right' }}
              onClick={(e) => { e.stopPropagation(); openClient(); }}
            >
              +{hidden} طلب آخر
            </button>
          )}
        </>
      ) : (
        canOpen && (
          <button
            className="btn btn-outline btn-sm"
            style={{ fontSize: 11, padding: '2px 8px' }}
            title="فتح فرصة من هذه المكالمة"
            onClick={(e) => { e.stopPropagation(); setShowStart(true); }}
          >
            <FiPlusCircle size={12} /> فتح فرصة
          </button>
        )
      )}

      {hasProfile ? (
        <button className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '2px 8px' }}
          title="ملف العميل في الـ CRM"
          onClick={(e) => { e.stopPropagation(); openClient(); }}>
          <FiUser size={12} /> الملف
        </button>
      ) : (
        <button className="btn btn-outline btn-sm"
          style={{ fontSize: 11, padding: '2px 8px', borderColor: '#f0b429', color: '#8a5a00' }}
          title="لا يوجد ملف لهذا العميل — احفظه أولاً"
          onClick={(e) => { e.stopPropagation(); setShowSave(true); }}>
          <FiUserPlus size={12} /> حفظ العميل
        </button>
      )}

      {showSave && (
        <CrmSaveClientModal
          phoneNumber={phone}
          // Seed the field with the 3CX display name when it is a real name
          // rather than the number repeated back.
          initialName={call.callerName && call.callerName !== call.callerNumber
            && !/^\d+$/.test(call.callerName) ? call.callerName : ''}
          onClose={() => setShowSave(false)}
          onSaved={onChanged}
        />
      )}

      {showStart && (
        <CrmStartOpportunityModal
          phoneNumber={phone}
          queue={call.queueName || call.callSource}
          sourceCallId={call.id}
          onClose={() => setShowStart(false)}
          onCreated={onChanged}
          onOpenOpportunity={openOpportunity}
        />
      )}

      {reassignFor && (
        <CrmReassignModal
          opportunityId={reassignFor.id}
          currentExtension={reassignFor.assigned_to_extension}
          onClose={() => setReassignFor(null)}
          onDone={onChanged}
        />
      )}
    </div>
  );
};

export default CrmCallCell;
